import { createEvent, createStore } from "effector"
import { keyBy, uniq, without } from "lodash"

import ArticleService from "services/ArticleService"
import Location from "models/Location"
import { Guid, IApiLink, ITranslations, Lmid } from "@/types"
import ReservationArticle from "models/ReservationArticle"
import ReservationCartArticle from "models/ReservationCartArticle"
import toastMessages from "./toastMessages"
import AssetService from "services/AssetService"
import ShippingOrder, { ShippingOrderStatus, ShippingOrderType } from "models/ShippingOrder"
import ReservationCartAsset from "models/ReservationCartAsset"
import ReservationAsset from "models/ReservationAsset"
import { AssetOwnershipType } from "models/Asset"
import AssetDisplayStatus from "models/AssetDisplayStatus"
import { ArticleType } from "config/constants"

export interface IArticleInfoCacheItem {
    articleId: Guid
    articleType: ArticleType
    articleNumber: string
    articleDescription: string
    articleDescriptionTranslations: ITranslations
    articleNo: string
    articleImageUri?: string
    articleThumbnailUri?: string
    articleFeatures?: ITranslations
}

export interface IAssetInfoCacheItem {
    assetId: Guid
    articleId: Guid
    articleNumber: string
    articleDescription: string
    articleDescriptionTranslations: ITranslations
    altArticleDescription?: string
    articleThumbnailUri?: string
    lmid: Lmid
    isLabeled: boolean
    isOperational: boolean
    location?: Location
    features?: ITranslations
    hasFeaturesOrFreightUnits: boolean
    warehouseId?: Guid
    profitCenterId?: Guid
    colorId?: Guid
    constructionYear?: number
    homebaseId?: Guid
    ownershipType?: AssetOwnershipType
    customerName?: string
    assetCollectionId?: Guid
    serialNumber?: string
}

export interface IArticleInfoCache {
    [articleId: Guid]: IArticleInfoCacheItem | undefined
}

export interface IAssetInfoCache {
    [assetId: Guid]: IAssetInfoCacheItem | undefined
}

interface IAssetStateOfStorageCache {
    [assetId: Guid]: AssetDisplayStatus
}


// Stores
const $articleInfoCache = createStore<IArticleInfoCache>({})
const $assetInfoCache = createStore<IAssetInfoCache>({})
const $assetStateOfStorageCache = createStore<IAssetStateOfStorageCache>({})
const $shippingOrdersCache = createStore<ShippingOrder[]>([])


// Events
const updateArticleInfoCache = createEvent<IArticleInfoCacheItem[]>()
const updateAssetInfoCache = createEvent<IAssetInfoCacheItem[]>()
const updateAssetStateOfStorageCache = createEvent<IAssetStateOfStorageCache>()
const unpdateShippingOrdersCache = createEvent<ShippingOrder[]>()
const reset = createEvent()


// Listeners
$articleInfoCache
    .on(updateArticleInfoCache, (state, payload) => ({
        ...state,
        ...keyBy(payload, 'articleId')
    }))
    .on(reset, () => ({}))

$assetInfoCache
    .on(updateAssetInfoCache, (state, payload) => ({
        ...state,
        ...keyBy(payload, 'assetId')
    }))
    .on(reset, () => ({}))

$assetStateOfStorageCache
    .on(updateAssetStateOfStorageCache, (state, payload) => ({
        ...state,
        ...payload,
    }))
    .on(reset, () => ({}))

$shippingOrdersCache
    .on(unpdateShippingOrdersCache, (_, payload) => payload)
    .on(reset, () => ([]))


// Utils

// fill articles and assets info into cart
export async function hydrateReservationConfiguration<ConfigurationItem extends ReservationArticle | ReservationCartArticle>({
    articles,
    useCache,
    linkGetShippingOrders,
}: {
    articles: ConfigurationItem[]
    useCache?: boolean
    linkGetShippingOrders?: IApiLink
}): Promise<ConfigurationItem[]> {
    const { unplannedShippings } = await hydrateReservationConfigurationShippingInfo({
        articles,
        useCache,
        linkGetShippingOrders,
    })

    await hydrateReservationConfigurationArticlesAndAssetsInfo<ConfigurationItem>({ articles, useCache, unplannedShippings })

    return articles
}

async function hydrateReservationConfigurationArticlesAndAssetsInfo<ConfigurationItem extends ReservationArticle | ReservationCartArticle>({
    articles,
    useCache,
    unplannedShippings,
}: {
    articles: ConfigurationItem[]
    useCache?: boolean
    unplannedShippings: ReservationAsset["shipping"]
}): Promise<void> {
    let articlesCache = useCache ? $articleInfoCache.getState() : {}
    let assetsCache = useCache ? $assetInfoCache.getState() : {}
    let assetStateOfStorageCache = useCache ? $assetStateOfStorageCache.getState() : {}

    try {
        const assetIdsToFetch: Guid[] = []
        const assetStatesOfStorageToFetch: Guid[] = []
        const articleIdsToFetch: Guid[] = []

        for (const article of articles) {
            for (const asset of article.assets) {
                if (!asset.assetId) continue
                if (!assetsCache[asset.assetId]) {
                    assetIdsToFetch.push(asset.assetId)
                }
                if (!assetStateOfStorageCache[asset.assetId]) {
                    assetStatesOfStorageToFetch.push(asset.assetId)
                }
            }

            if (!articlesCache[article.articleId]) {
                articleIdsToFetch.push(article.articleId)
            }
        }

        const unplannedAssetIds: Guid[] = unplannedShippings?.map(ua => ua.shippingOrderAsset.assetId) || []

        for (const unplannedAssetId of unplannedAssetIds) {
            if (!assetsCache[unplannedAssetId]) {
                assetIdsToFetch.push(unplannedAssetId)
            }
            if (!assetStateOfStorageCache[unplannedAssetId]) {
                assetStatesOfStorageToFetch.push(unplannedAssetId)
            }
        }

        const newArticleCacheItems: IArticleInfoCacheItem[] = []
        const newAssetCacheItems: IAssetInfoCacheItem[] = []
        let newAssetStateOfStorageCacheItems: IAssetStateOfStorageCache = {}

        if (assetIdsToFetch.length > 0) {
            const assetArticlesResp = await ArticleService.getAssetArticles({
                assetIds: assetIdsToFetch,
                pageSize: assetIdsToFetch.length,
            })

            for (const aa of assetArticlesResp.list) {
                newAssetCacheItems.push({
                    articleId: aa.articleId,
                    assetId: aa.assetId,
                    articleNumber: aa.articleNo || "",
                    articleDescription: aa.articleDescription || "",
                    articleDescriptionTranslations: aa.articleDescriptionTranslations || {},
                    altArticleDescription: aa.altArticleDescription || "",
                    articleThumbnailUri: aa.thumbnailUri || undefined,
                    lmid: aa.lmid,
                    isLabeled: aa.isLabeled,
                    isOperational: aa.isOperational,
                    location: aa.latitude && aa.longitude ? new Location({
                        latitude: aa.latitude,
                        longitude: aa.longitude,
                    }) : undefined,
                    features: aa.features,
                    hasFeaturesOrFreightUnits: aa.hasFeaturesOrFreightUnits,
                    warehouseId: aa.warehouseId,
                    colorId: aa.colorId,
                    constructionYear: aa.constructionYear,
                    homebaseId: aa.homebaseId,
                    ownershipType: aa.ownershipType,
                    customerName: aa.customerName,
                    assetCollectionId: aa.assetCollectionId,
                    serialNumber: aa.serialNumber
                })

                const existingArticle = articles.find(a => a.articleId === aa.articleId)
                if (existingArticle) continue
                articleIdsToFetch.push(aa.articleId)
                const reservationArticle = new ReservationCartArticle({
                    dto: {
                        articleId: aa.articleId,
                        quantity: 0,
                        assets: []
                    }
                })
                articles.push(reservationArticle as ConfigurationItem)
            }
        }

        if (assetStatesOfStorageToFetch.length > 0) {
            try {
                const assetStatesOfStorageResp = await AssetService.getAssetDisplayStatuses(assetStatesOfStorageToFetch)
                newAssetStateOfStorageCacheItems = {
                    ...newAssetStateOfStorageCacheItems,
                    ...assetStatesOfStorageResp,
                }
            } catch (e) {
                console.log(e)
                toastMessages.showSystemError()
            }
        }

        if (articleIdsToFetch.length > 0) {
            const uniqArticleIdsToFetch = uniq(articleIdsToFetch)
            const articleAssetsResp = await ArticleService.getArticleAssets({
                articleIds: uniqArticleIdsToFetch,
                pageSize: uniqArticleIdsToFetch.length,
            })

            if (articleAssetsResp) {
                for (const aa of articleAssetsResp.list) {
                    newArticleCacheItems.push({
                        articleId: aa.id,
                        articleType: aa.articleType,
                        articleFeatures: aa.features,
                        articleNumber: aa.articleNo,
                        articleDescription: aa.description,
                        articleDescriptionTranslations: aa.descriptionTranslations,
                        articleNo: aa.articleNo,
                        articleThumbnailUri: aa.thumbnailUri,
                        articleImageUri: aa.imageUri,
                    })
                }
            }
        }

        articlesCache = {
            ...articlesCache,
            ...keyBy(newArticleCacheItems, "articleId"),
        }

        assetsCache = {
            ...assetsCache,
            ...keyBy(newAssetCacheItems, "assetId"),
        }

        assetStateOfStorageCache = {
            ...assetStateOfStorageCache,
            ...newAssetStateOfStorageCacheItems,
        }

        if (useCache) {
            updateArticleInfoCache(newArticleCacheItems)
            updateAssetInfoCache(newAssetCacheItems)
            updateAssetStateOfStorageCache(newAssetStateOfStorageCacheItems)
        }

        for (const article of articles) {
            if (!article.isHydrated) {
                const cachedArticle = articlesCache[article.articleId]
                article.articleType = cachedArticle?.articleType
                article.articleDescription = cachedArticle?.articleDescription
                article.articleDescriptionTranslations = cachedArticle?.articleDescriptionTranslations
                article.articleFeatures = cachedArticle?.articleFeatures
                article.articleNo = cachedArticle?.articleNo
                article.articleThumbnailUri = cachedArticle?.articleThumbnailUri
                article.articleImageUri = cachedArticle?.articleImageUri
            }
            for (const asset of article.assets) {
                asset.articleId = article.articleId
                if (!asset.isHydrated && asset.assetId) {
                    const cachedAsset = assetsCache[asset.assetId]
                    if (!cachedAsset) continue
                    asset.articleNumber = cachedAsset?.articleNumber
                    asset.articleDescription = cachedAsset?.articleDescription
                    asset.articleDescriptionTranslations = cachedAsset?.articleDescriptionTranslations
                    asset.altArticleDescription = cachedAsset?.altArticleDescription
                    asset.articleThumbnailUri = cachedAsset?.articleThumbnailUri
                    asset.lmid = cachedAsset?.lmid
                    asset.isLabeled = cachedAsset?.isLabeled
                    asset.isOperational = cachedAsset?.isOperational
                    asset.location = cachedAsset?.location
                    asset.warehouseId = cachedAsset?.warehouseId
                    asset.profitCenterId = cachedAsset?.profitCenterId
                    asset.features = cachedAsset?.features
                    asset.hasFeaturesOrFreightUnits = cachedAsset?.hasFeaturesOrFreightUnits
                    asset.colorId = cachedAsset?.colorId
                    asset.constructionYear = cachedAsset?.constructionYear
                    asset.homebaseId = cachedAsset?.homebaseId
                    asset.ownershipType = cachedAsset?.ownershipType
                    asset.stateOfStorage = assetStateOfStorageCache[asset.assetId]
                    asset.customerName = cachedAsset?.customerName
                    asset.assetCollectionId = cachedAsset?.assetCollectionId
                    asset.serialNumber = cachedAsset?.serialNumber
                }
            }
        }

        if (unplannedShippings && unplannedShippings.length > 0) {
            for (const unplannedShipping of unplannedShippings) {
                const cachedAsset = assetsCache[unplannedShipping.shippingOrderAsset.assetId]
                if (!cachedAsset) continue
                for (const article of articles) {
                    if (article.articleId === cachedAsset.articleId) {
                        const existingReservationAsset = article.assets.find(a => a.assetId === unplannedShipping.shippingOrderAsset.assetId)

                        if (existingReservationAsset) {
                            if (!existingReservationAsset.shipping) {
                                existingReservationAsset.shipping = []
                            }
                            existingReservationAsset.shipping.push(unplannedShipping)
                        } else {
                            const reservationAsset = new ReservationCartAsset({
                                assetId: cachedAsset.assetId,
                                hasUserAssignment: true,
                                assetUnavailable: false,
                            })

                            reservationAsset.articleId = cachedAsset.articleId
                            reservationAsset.articleNumber = cachedAsset.articleDescription
                            reservationAsset.articleDescription = cachedAsset.articleDescription
                            reservationAsset.articleDescriptionTranslations = cachedAsset.articleDescriptionTranslations
                            reservationAsset.altArticleDescription = cachedAsset.altArticleDescription
                            reservationAsset.articleThumbnailUri = cachedAsset.articleThumbnailUri
                            reservationAsset.lmid = cachedAsset.lmid
                            reservationAsset.isLabeled = cachedAsset.isLabeled
                            reservationAsset.isOperational = cachedAsset.isOperational
                            reservationAsset.location = cachedAsset.location
                            reservationAsset.warehouseId = cachedAsset.warehouseId
                            reservationAsset.profitCenterId = cachedAsset.profitCenterId
                            reservationAsset.features = cachedAsset.features
                            reservationAsset.hasFeaturesOrFreightUnits = cachedAsset.hasFeaturesOrFreightUnits
                            reservationAsset.colorId = cachedAsset.colorId
                            reservationAsset.constructionYear = cachedAsset.constructionYear
                            reservationAsset.homebaseId = cachedAsset.homebaseId
                            reservationAsset.ownershipType = cachedAsset.ownershipType
                            reservationAsset.stateOfStorage = assetStateOfStorageCache[cachedAsset.assetId]
                            reservationAsset.customerName = cachedAsset.customerName
                            reservationAsset.assetCollectionId = cachedAsset.assetCollectionId
                            reservationAsset.unplanned = true
                            reservationAsset.shipping = [unplannedShipping]
                            reservationAsset.serialNumber = cachedAsset.serialNumber

                            article.assets.push(reservationAsset)
                        }
                    }
                }
            }
        }
    } catch (e) {
        toastMessages.showSystemError()
    }
}


async function hydrateReservationConfigurationShippingInfo<ConfigurationItem extends ReservationArticle | ReservationCartArticle>({
    articles,
    useCache,
    linkGetShippingOrders,
}: {
    articles: ConfigurationItem[]
    useCache?: boolean
    linkGetShippingOrders?: IApiLink
}): Promise<{
    unplannedShippings: ReservationAsset["shipping"]
}> {
    let unplannedShippings: ReservationAsset["shipping"] = []

    if (!linkGetShippingOrders && !useCache) {
        return { unplannedShippings }
    }

    try {
        const shippingOrders = linkGetShippingOrders
            ? await AssetService.getReservationShippingOrders(linkGetShippingOrders)
            : $shippingOrdersCache.getState()

        if (linkGetShippingOrders && useCache) {
            unpdateShippingOrdersCache(shippingOrders)
        }

        let assetShippings: ReservationAsset["shipping"] = []

        for (const shippingOrder of shippingOrders) {
            for (const shippingOrderAsset of shippingOrder.assets) {
                assetShippings.push({
                    shippingOrderType: shippingOrder.type,
                    shippingOrderStatus: shippingOrder.status,
                    shippingOrderAsset,
                })
            }
        }

        for (const article of articles) {
            for (const asset of article.assets) {
                asset.shipping = []
                for (const assetShipping of assetShippings) {
                    if (assetShipping.shippingOrderAsset.assetId !== asset.assetId) continue
                    asset.shipping.push(assetShipping)
                    assetShippings = without(assetShippings, assetShipping)
                }
            }
        }

        // in the shippingOrdersAssets array only unplanned assets left,
        // except the ones that were not released/stored in closed shipping orders
        unplannedShippings = assetShippings.filter(sh => {
            return sh.shippingOrderStatus !== ShippingOrderStatus.completed
                || (sh.shippingOrderType === ShippingOrderType.delivery && !!sh.shippingOrderAsset.releaseDate)
                || (sh.shippingOrderType === ShippingOrderType.return && !!sh.shippingOrderAsset.storeDate)
        })
    } catch (e) {
        toastMessages.showSystemError()
    }


    return {
        unplannedShippings,
    }
}



export function sortConfigurationAssets({
    assets,
    areFixedAssetsRequired,
}: {
    assets: ReservationCartAsset[]
    areFixedAssetsRequired: boolean
}): ReservationCartAsset[] {
    const fixedRequiredNotSelected: ReservationCartAsset[] = []
    const shortages: ReservationCartAsset[] = []
    const unplanned: ReservationCartAsset[] = []
    const rest: ReservationCartAsset[] = []

    for (const asset of assets) {
        const isFixedRequiredNotSelected = areFixedAssetsRequired && asset.isAutomaticSelection
        if (isFixedRequiredNotSelected) {
            fixedRequiredNotSelected.push(asset)
            continue
        } else if (!asset.assetId) {
            shortages.push(asset)
            continue
        }
        if (asset.unplanned) {
            unplanned.push(asset)
            continue
        }
        rest.push(asset)
    }

    return [
        ...rest,
        ...fixedRequiredNotSelected,
        ...shortages,
        ...unplanned,
    ]
}


export default {
    reset,
    $shippingOrdersCache,
}
